import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useCRM } from '../context/CRMContext';
import { format } from 'date-fns';
import Card from '../components/UI/Card';
import Button from '../components/UI/Button';
import Modal from '../components/UI/Modal';
import ServiceForm from '../components/Forms/ServiceForm';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiEdit, FiTrash2, FiPackage } = FiIcons;

const Services = () => {
  const { services, addService, updateService, deleteService } = useCRM();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);

  const handleAddService = () => {
    setEditingService(null);
    setIsModalOpen(true);
  };

  const handleEditService = (service) => {
    setEditingService(service);
    setIsModalOpen(true);
  };

  const handleSubmit = (data) => {
    if (editingService) {
      updateService(editingService.id, data);
    } else {
      addService(data);
    }
    setIsModalOpen(false);
    setEditingService(null);
  };

  const handleDeleteService = (id) => {
    if (window.confirm('Are you sure you want to delete this service? This may affect existing works using this service.')) {
      deleteService(id);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">Services</h1>
        <Button onClick={handleAddService}>
          <SafeIcon icon={FiPlus} className="h-4 w-4 mr-2" />
          Add Service
        </Button>
      </div>

      {services?.length === 0 ? (
        <Card className="p-12 text-center">
          <SafeIcon icon={FiPackage} className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No services yet</h3>
          <p className="text-gray-500 mb-4">Add services to organize your work offerings.</p>
          <Button onClick={handleAddService}>
            <SafeIcon icon={FiPlus} className="h-4 w-4 mr-2" />
            Add Service
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {services?.map((service, index) => (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card className="p-4 hover:shadow-lg transition-shadow">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {service.name}
                    </h3>
                    <p className="text-xs text-gray-500 mt-1">
                      Added {format(new Date(service.createdAt), 'MMM dd, yyyy')}
                    </p>
                  </div>
                  <div className="flex space-x-1 ml-2">
                    <button
                      onClick={() => handleEditService(service)}
                      className="text-gray-400 hover:text-primary-600 transition-colors p-1"
                    >
                      <SafeIcon icon={FiEdit} className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteService(service.id)}
                      className="text-gray-400 hover:text-red-600 transition-colors p-1"
                    >
                      <SafeIcon icon={FiTrash2} className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingService ? 'Edit Service' : 'Add New Service'}
        size="md"
      >
        <ServiceForm
          service={editingService}
          onSubmit={handleSubmit}
          onCancel={() => setIsModalOpen(false)}
        />
      </Modal>
    </div>
  );
};

export default Services;